/**
 * Le format des liens courts, partagé serveur ↔ back-office.
 *
 * Il vit dans les contrats parce que le **budget** des variables de lien en
 * dépend : l'éditeur de modèles mesure un SMS au pire cas, et ce pire cas est
 * la longueur maximale d'un lien court. Deux définitions de cette longueur
 * feraient mentir le compteur exactement le jour où le message déborde.
 */
/**
 * Alphabet des slugs : ni `0`/`O`, ni `1`/`I`/`l`. Un lien court finit parfois
 * recopié à la main depuis un écran de téléphone, et une ambiguïté de fonte y
 * mène sur une page morte.
 *
 * 57 symboles, 7 caractères : 1,9 × 10¹² combinaisons. Le tirage est aléatoire
 * et non séquentiel — un slug incrémental laisserait n'importe qui énumérer les
 * liens de paiement des autres clients.
 */
export const SHORT_LINK_SLUG_ALPHABET = '23456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';
export const SHORT_LINK_SLUG_LENGTH = 7;
/** Le segment fixe entre l'hôte et le slug : `msvy.fr` + `/l/` + `Ab3dK9x`. */
export const SHORT_LINK_PATH_PREFIX = '/l/';
/**
 * Longueur maximale d'un lien court rendu, budget des variables `paymentLink`
 * et `clientSpaceUrl`.
 *
 * C'est un **plafond**, calé sur l'hôte le plus long réellement en service :
 * `r.msvy.fr`, celui de la recette, soit 9 caractères plus `/l/` et 7 de slug.
 * La production, sur `msvy.fr`, rend 17.
 *
 * Le domaine court est la raison d'être de ce chiffre. Sur l'hôte de l'API il
 * valait 40 — la recette, `staging.api.massiliavoyages.fr`, tenait le plafond à
 * elle seule — et baisser la seule production n'aurait rien donné : le budget
 * suit le plus long des deux. Les deux environnements ont donc bougé ensemble,
 * et les modèles ont gagné 21 caractères : 141 pour le texte, contre 120 avant
 * et 21 du temps où la variable portait le lien de paiement complet.
 *
 * Le serveur **refuse de démarrer** si le domaine configuré dépasse ce budget.
 * Un budget faux ne se découvre pas autrement qu'en production, sur un SMS
 * tronqué. La boucle locale y échappe (cf. `ShortLinkService`) : `localhost`
 * est plus long que le budget et ne part dans aucun SMS.
 */
export const SHORT_LINK_MAX_CHARS = 19;
/** Longueur qu'atteindra un lien servi par cet hôte, slug compris. */
export function shortLinkLength(base) {
    return shortLinkUrl(base, 'x'.repeat(SHORT_LINK_SLUG_LENGTH)).length;
}
/**
 * Le lien tel qu'il part dans le SMS, **sans** `https://`.
 *
 * Les téléphones rendent `msvy.fr/l/Ab3dK9x` cliquable sans le préfixe, qui
 * coûterait 8 des 160 caractères sans rien apporter au client. `base` est donc
 * un hôte nu, pas une URL.
 */
export function shortLinkUrl(base, slug) {
    return `${base.replace(/\/+$/, '')}${SHORT_LINK_PATH_PREFIX}${slug}`;
}
const SLUG_PATTERN = new RegExp(`^[${SHORT_LINK_SLUG_ALPHABET}]{${SHORT_LINK_SLUG_LENGTH}}$`);
/**
 * Un slug bien formé. La route de redirection s'en sert pour écarter les
 * requêtes de balayage avant d'atteindre la base — un scanner qui essaie
 * `/l/wp-admin` ne doit pas coûter une requête SQL.
 */
export function isShortLinkSlug(value) {
    return SLUG_PATTERN.test(value);
}
